// Pure helpers for the Big clients worklist: how a committed budget reads, and
// what its column sorts on.
//
// Kept out of the page so both are unit-testable. The budget is the number the
// whole desk decides on, and a wrong magnitude is the difference between a big
// fish and a rounding error. Since the engagement structure (0156) that number
// can be a RATE — the desk row carries it as committed, and both helpers
// delegate to the shared `format.ts` logic so a retainer reads and sorts here
// exactly as it does in the Explorer tables (and as the alert that paged the
// desk judged it: one month of the rate, never an invented total).

import {
  budgetSortValue as sharedBudgetSortValue,
  formatBudget as sharedFormatBudget,
  type ProjectBudget,
} from "./format";

/** The committed-money fields a Big-clients row carries (all straight off
 *  `search_preferences`; flags only ever fire on USD figures, so there is no
 *  currency column to carry). */
export interface BigClientBudget {
  budgetMinCents: number | null;
  budgetMaxCents: number | null;
  budgetHourlyCents?: number | null;
  rateAmountCents?: number | null;
  rateUnit?: "hour" | "day" | "week" | "month" | "year" | "deliverable" | null;
  rateDeliverableNoun?: string | null;
  ratePerPerson?: boolean;
  cadenceCount?: number | null;
  cadenceUnit?: "week" | "month" | "year" | null;
  rateAmountMinCents?: number | null;
  cadenceCountMin?: number | null;
}

function asProjectBudget(alert: BigClientBudget): ProjectBudget {
  return {
    minCents: alert.budgetMinCents,
    maxCents: alert.budgetMaxCents,
    hourlyCents: alert.budgetHourlyCents ?? null,
    currency: "USD",
    rateAmountCents: alert.rateAmountCents ?? null,
    rateUnit: alert.rateUnit ?? null,
    rateDeliverableNoun: alert.rateDeliverableNoun ?? null,
    ratePerPerson: alert.ratePerPerson ?? false,
    cadenceCount: alert.cadenceCount ?? null,
    cadenceUnit: alert.cadenceUnit ?? null,
    rateAmountMinCents: alert.rateAmountMinCents ?? null,
    cadenceCountMin: alert.cadenceCountMin ?? null,
  };
}

/** Committed money as the desk column prints it: `$1,200`, `$800 - $1,200`,
 *  or a rate in the client's own unit (`$1,500/mo`, `$50/h, 20h/mo`). Both
 *  ends absent prints "-" rather than "$0", because no-figure and zero are
 *  different facts and one of them would look like a bug in the alert. */
export function formatBudget(alert: BigClientBudget): string {
  return sharedFormatBudget(asProjectBudget(alert));
}

/** Sort/compare value for the budget column: the committed total, or one month
 *  of the committed rate — the same effective figure the alert tier judged, so
 *  the desk orders by how big the fish actually is. */
export function budgetSortValue(alert: BigClientBudget): number | null {
  return sharedBudgetSortValue(asProjectBudget(alert));
}

// --- the handoff desk ---------------------------------------------------------
//
// At the top budget tier a handoff stops being "a specialist will reach out" and
// becomes a MEETING the client books themselves, so the backend routes them to a
// desk and shows them that desk's calendar. Both facts live on the project
// (`projects.metadata_json`) and reached no admin surface at all until now
// (Monday 3168800870).

/** The desks a handoff can route to. Mirrors `HANDOFF_TRACKS` in
 *  backend/recruiter/handoff_routing.py, where the vocabulary is a contract:
 *  the value picks which calendar a client is sent to. */
export const HANDOFF_DESKS = ["sales", "bsm", "sourcing"] as const;

const DESK_LABELS: Record<(typeof HANDOFF_DESKS)[number], string> = {
  sales: "Sales",
  bsm: "BSM",
  sourcing: "Sourcing",
};

/** The desk's human label, or null when this project was routed to none.
 *
 *  Null is the ordinary answer, not an error: routing is gated on the same
 *  committed-budget tier as the automatic handoff, so a project that never
 *  qualified carries no desk. An UNRECOGNISED value is null for a different
 *  reason and deliberately reads the same way, mirroring `parse_handoff_track`
 *  on the backend: the value comes out of a free-form metadata bag, and naming
 *  a desk that does not exist is worse for the operator than naming none. */
export function handoffDeskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return DESK_LABELS[value as (typeof HANDOFF_DESKS)[number]] ?? null;
}

/** Where this client got to on their handoff meeting.
 *
 *  `none` means no desk was routed, so there was never a meeting to book — kept
 *  apart from `not_booked` because "ignored the invitation" and "never got one"
 *  are opposite facts, and only the first is a job for an operator. The desk is
 *  what decides that, which is why a booking flag without a desk still reads as
 *  `none` rather than as a booking.
 *
 *  `booked` is a CLAIM from the client, not proof: their browser reports the
 *  embedded scheduler's confirmation, and an "I already booked" link covers the
 *  bookings it cannot see. Good enough to tell a desk where to look, not an
 *  attendance record. */
export function meetingBookingState(alert: {
  handoffTrack?: string | null;
  handoffMeetingBooked?: boolean;
}): "booked" | "not_booked" | "none" {
  if (handoffDeskLabel(alert.handoffTrack) === null) return "none";
  return alert.handoffMeetingBooked ? "booked" : "not_booked";
}
